import { maintenance } from '../api';
import { createPageHeader } from '../components/form';
import { createDataTable, type Column } from '../components/table';
import { navigate } from '../router';
import type { MaintenanceIssue } from '../types';

function fmt(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(n);
}

export async function maintenancePage(): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('Maintenance'));

  // Filters
  const filters = document.createElement('div');
  filters.className = 'flex gap-4 mb-6 flex-wrap';
  filters.innerHTML = `
    <select id="status-filter" class="input-field w-auto">
      <option value="">All Statuses</option>
      <option value="open">Open</option>
      <option value="in-progress">In Progress</option>
      <option value="completed">Completed</option>
    </select>
    <select id="priority-filter" class="input-field w-auto">
      <option value="">All Priorities</option>
      <option value="urgent">Urgent</option>
      <option value="high">High</option>
      <option value="medium">Medium</option>
      <option value="low">Low</option>
    </select>
  `;
  page.appendChild(filters);

  const tableContainer = document.createElement('div');
  page.appendChild(tableContainer);

  const load = async () => {
    const status = (page.querySelector('#status-filter') as HTMLSelectElement).value;
    const priority = (page.querySelector('#priority-filter') as HTMLSelectElement).value;
    const issues = await maintenance.all(status || undefined, priority || undefined);
    renderTable(tableContainer, issues);
  };

  filters.querySelectorAll('select').forEach(s => s.addEventListener('change', load));
  await load();
  return page;
}

function renderTable(container: HTMLElement, issues: MaintenanceIssue[]): void {
  container.innerHTML = '';
  const cols: Column<MaintenanceIssue>[] = [
    { label: 'Title', render: r => r.title || r.description, className: 'max-w-xs truncate' },
    { label: 'Property', render: r => `<a href="#/properties/${r.property_id}" class="text-primary-600 hover:underline">${r.property_address || r.property_id.substring(0, 8) + '...'}</a>` },
    { label: 'Priority', render: r => `<span class="badge priority-${r.priority}">${r.priority}</span>` },
    { label: 'Status', render: r => `<span class="badge badge-${r.status === 'completed' ? 'green' : r.status === 'in-progress' ? 'yellow' : 'gray'}">${r.status}</span>` },
    { label: 'Reported', key: 'date_reported' },
    { label: 'Quoted', render: r => r.quoted_amount ? fmt(r.quoted_amount) : '—', className: 'text-right' },
    { label: 'Actual', render: r => r.actual_amount ? fmt(r.actual_amount) : '—', className: 'text-right' },
  ];
  container.appendChild(createDataTable(cols, issues, {
    emptyText: 'No maintenance issues found.',
    onRowClick: (row) => navigate(`#/properties/${row.property_id}/maintenance/${row.id}`),
  }));
}
